import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from '../context/I18nContext';

export const Privacy = () => {
  const { t } = useTranslation();

  const sections = [
    'privacyStatement',
    'personalInfo',
    'businessInfo',
    'accountRegistration',
    'supplierVerification',
    'restaurantAccounts',
    'orders',
    'payments',
    'paymentProviders',
    'vatInformation',
    'billing',
    'cookies',
    'analytics',
    'security',
    'dataStorage',
    'gdprCompliance',
    'userRights',
    'dataRetention',
    'internationalTransfers',
    'marketingCommunications',
    'intellectualProperty',
    'platformRules',
    'supplierTerms',
    'buyerResponsibilities',
    'productListings',
    'reviews',
    'liability',
    'disputes',
    'governingLaw',
    'contactInfo'
  ];

  return (
    <div className="min-h-screen bg-[#FCFAF8] pt-48 pb-40">
      <div className="container mx-auto px-6 max-w-4xl">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
        >
          {/* Header Section - Perfectly Aligned */}
          <header className="mb-32 border-b border-zinc-100 pb-20">
            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.8 }}
              className="text-5xl md:text-6xl font-medium text-zinc-900 tracking-[-0.02em] mb-10 leading-[1.1]"
            >
              {t('privacy.title')}
            </motion.h1>
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-8">
              <p className="text-[10px] font-black uppercase tracking-[0.4em] text-[#D4B483]">
                {t('privacy.intro')}
              </p>
              <div className="flex gap-4">
                <span className="w-12 h-px bg-zinc-200 self-center" />
                <span className="text-[9px] font-black text-zinc-300 uppercase tracking-widest">Document Ref: FR-LEG-2026-07</span>
              </div>
            </div>
          </header>

          {/* Content Sections - One clean vertical alignment line */}
          <div className="space-y-32">
            {sections.map((sectionKey, idx) => (
              <motion.section
                key={sectionKey}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.8 }}
                className="max-w-3xl"
              >
                <div className="flex items-baseline gap-4 mb-8">
                  <span className="text-[9px] font-black text-[#D4B483] tabular-nums opacity-60">
                    {(idx + 1).toString().padStart(2, '0')}
                  </span>
                  <h2 className="text-2xl md:text-3xl font-semibold text-zinc-900 tracking-tight leading-tight">
                    {t(`privacy.sections.${sectionKey}.title`)}
                  </h2>
                </div>
                
                {/* Removed pl-12 and border-l to ensure perfect left margin alignment */}
                <div className="space-y-8">
                  {t(`privacy.sections.${sectionKey}.content`).split('\n\n').map((paragraph, pIdx) => (
                    <p 
                      key={pIdx} 
                      className="text-zinc-500 leading-[1.85] text-lg font-light tracking-normal"
                    >
                      {paragraph.trim()}
                    </p>
                  ))}
                </div>
              </motion.section>
            ))}
          </div>

          {/* Legal Footer - Perfectly Aligned */}
          <footer className="mt-48 pt-24 border-t border-zinc-100">
            <div className="flex flex-col md:flex-row justify-between items-start gap-12">
              <div className="max-w-xs">
                <div className="text-xl font-bold tracking-tight text-zinc-900 flex items-center gap-2 mb-6">
                  <div className="w-7 h-7 bg-zinc-900 rounded-lg flex items-center justify-center">
                    <span className="text-white text-sm font-black">f</span>
                  </div>
                  Foodrolla
                </div>
                <p className="text-[10px] font-bold text-zinc-400 leading-relaxed uppercase tracking-widest">
                  Professional B2B Marketplace. Facilitating European Gastronomy.
                </p>
              </div>
              
              <div className="text-left md:text-right">
                <p className="text-[9px] font-black uppercase tracking-[0.4em] text-zinc-300 mb-4">
                  OFFICIAL RECORD
                </p>
                <p className="text-xs text-zinc-400 font-medium">
                  Amsterdam Commercial Registry
                </p>
                <p className="text-xs text-zinc-400 font-medium">
                  VAT: NL862145789B01
                </p>
              </div>
            </div>
          </footer>
        </motion.div>
      </div>
    </div>
  );
};